export function normalizeBackendTimestampToIsoUtc(raw: string) {
  // Backend / Postgres often return timestamps like:
  // - `2026-01-31T19:51:04.641154` (naive UTC, microseconds)
  // - `2026-07-30 17:41:00.123456+00` (space + short offset)
  // JS Date treats "no timezone" as local time and rejects some Postgres forms.
  let s = String(raw || '').trim()
  if (!s) return s

  // Postgres timestamptz::text uses a space between date and time.
  s = s.replace(' ', 'T')

  // Short offsets (`+00`, `-05`) → RFC3339 (`+00:00`, `-05:00`).
  s = s.replace(/([+-]\d{2})$/, '$1:00')
  // Compact offsets (`+0000`, `-0500`) → `+00:00` / `-05:00`.
  s = s.replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3')

  // Trim/pad fractional seconds to milliseconds (3 digits) for safer parsing.
  // - ".641154" -> ".641"
  // - ".6"      -> ".600"
  // - ".64"     -> ".640"
  s = s.replace(/(\.\d{3})\d+/, '$1')
  s = s.replace(/(\.\d)(?=Z$|[+-]\d{2}:?\d{2}$|$)/, '$100')
  s = s.replace(/(\.\d{2})(?=Z$|[+-]\d{2}:?\d{2}$|$)/, '$10')

  // If no timezone/offset present, assume UTC.
  if (!/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z'
  return s
}

export function formatRelative(isoLike: string) {
  const d = new Date(normalizeBackendTimestampToIsoUtc(isoLike))
  const ms = Date.now() - d.getTime()
  if (!Number.isFinite(ms)) return ''
  if (ms < 0) return 'just now'
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function formatExactTime(isoLike: string): string {
  const raw = String(isoLike || '').trim()
  if (!raw) return '—'
  const d = new Date(normalizeBackendTimestampToIsoUtc(raw))
  if (!Number.isFinite(d.getTime())) return '—'
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
}

/** Admin UI date formatting in the viewer's local timezone. */
export function formatAdminDate(isoLike: string | null | undefined, includeTime = false): string {
  const raw = String(isoLike || '').trim()
  if (!raw) return '—'
  const d = new Date(normalizeBackendTimestampToIsoUtc(raw))
  if (!Number.isFinite(d.getTime()) || Number.isNaN(d.getTime())) return '—'
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const day = d.toLocaleString('en-US', { day: 'numeric', timeZone })
  const month = d.toLocaleString('en-US', { month: 'short', timeZone })
  const year = d.toLocaleString('en-US', { year: 'numeric', timeZone })
  if (!includeTime) return `${day} ${month}, ${year}`
  const time = d.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  })
  return `${day} ${month}, ${year} ${time}`
}
